/**
 * @fileoverview `/:naddr` -- the canonical page route.
 *
 * The addressable coordinate is the page's durable name, so it is what sits in
 * the URL. Not the blob hash and not the index event id: both change on every
 * edit, which would break every link anyone had ever shared. The readable form
 * `pages.cloistr.xyz/<author>/<slug>` RESOLVES to this route -- `ReadableRoute`
 * turns two segments into an address and then renders this component. It is
 * never the other way round and there is no redirect, because a name service
 * being down must not take other people's links with it.
 *
 * THE FORMAT SPLIT LIVES HERE AND NOWHERE ELSE. `loadPage` returns a body and
 * says nothing about what to do with it -- both formats are fetched and
 * hash-verified by the same code. This component is the one place that decides
 * markdown goes through the sanitiser into OUR document while HTML is given a
 * document of its own with every capability removed. Keeping the two treatments
 * adjacent is deliberate: if a third format is ever proposed, the choice it
 * forces is visible in one file rather than spread across the loader and the
 * renderer.
 */

import { useNostrAuth } from '@cloistr/auth';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ExportLink } from '@/components/ExportLink';
import { PageBody } from '@/components/PageBody';
import { PageUnavailable } from '@/components/PageUnavailable';
import { SandboxedHtml } from '@/components/SandboxedHtml';
import { TableOfContents } from '@/components/TableOfContents';
import { useColourScheme } from '@/components/useColourScheme';
import { WidthToggle, useReaderWidth } from '@/components/WidthToggle';
import { encodeProfile } from '@/services/nostr/identifiers';
import { authorPath, editPath, pagePath } from '@/services/nostr/pageLinks';
import { loadPage, type PageLoad } from '@/services/pages/loadPage';
import { loadTheme } from '@/services/pages/loadTheme';
import { themeStyle, type PageTheme } from '@/services/pages/theme';
import { renderMarkdown } from '@/services/render/markdown';

/**
 * The address arrives as a prop rather than from `useParams` because the URL
 * segment is not always a page -- `SegmentRoute` decodes it first and sends
 * authors elsewhere. It also makes this component testable without a router.
 */
export function PageRoute({
  naddr,
  showCanonical = false,
}: {
  naddr: string;
  /**
   * Set by the readable route, which arrived here by resolving a name. That
   * form depends on our name service and the address does not, so a reader who
   * is never shown the address can only ever share the fragile one.
   */
  showCanonical?: boolean;
}) {
  const [load, setLoad] = useState<PageLoad | null>(null);
  /**
   * The page's theme, or null until it arrives and forever if it never does.
   *
   * A SECOND STATE RATHER THAN PART OF THE LOAD, which is the decision that
   * keeps a slow relay from costing a reader the article. The body is already
   * in hand when this starts; if the theme fetch were inside `loadPage`, a
   * page whose theme relay is unreachable would show a spinner for the whole
   * relay timeout with the text sitting there unshown. So the page renders in
   * default tokens and restyles when the theme lands. The cost is a brief
   * moment of unstyled prose, which is the right way round for something whose
   * job is to be read.
   */
  const [theme, setTheme] = useState<PageTheme | null>(null);
  const scheme = useColourScheme();
  const [wide, setWide] = useReaderWidth();
  // Read purely to decide whether to offer the author their own edit link. No
  // relay traffic, no gate and no prompt: a reader with no key gets the page
  // exactly as before, which is the only reason it is safe to consult auth on
  // the reading path at all.
  const { authState, signer } = useNostrAuth();

  /**
   * THE EFFECT BELOW KEYS ON THE IDENTITY, NEVER ON THE SIGNER OBJECT, and that
   * is a defence rather than a preference. A signed-in reader could not open a
   * page at all: 6,425 relay subscriptions in ten seconds, a screen stuck on
   * "Loading...", and our own relay diagnosing it in as many words -- `CLOSED
   * ... "rate-limited: there is a bug in the client, no one should be making so
   * many requests"`.
   *
   * THE CAUSE IS A SELF-SUSTAINING EFFECT LOOP IN `@cloistr/auth`, and the
   * precise shape matters because the obvious description of it is wrong. The
   * signer is NOT minted during render -- it comes from `useState` and would be
   * referentially stable on its own. What happens instead, all in the package's
   * `context.js`: the session-restore effect calls the NIP-07 connect handler,
   * which mints a signer and dispatches CONNECTED; the reducer rebuilds
   * `authState.keys` through `upsertKey`, which returns a NEW ARRAY on both its
   * branches even when the content is identical; `authState.keys` is a
   * dependency of the connect handler, which is a dependency of the restore
   * callback, which is a dependency of the restore effect. So the effect
   * re-runs, mints another signer, and the cycle closes.
   *
   * MEASURED AGAINST THE PACKAGE ALONE, with no code of ours in the tree:
   * 56,742 renders and 28,370 distinct signer objects in 1.5 seconds, against
   * ONE render in the same harness with nothing in storage. The control is the
   * discrimination: the loop needs a RESTORED SESSION, so a signed-out visitor
   * never enters it -- which is why every browser proof missed this, since they
   * all read as strangers.
   *
   * WHAT THE LOAD ACTUALLY DEPENDS ON IS WHICH KEY IS ACTIVE, not which object
   * carries it: the signer's one effect here is that a private page opens for
   * the person entitled to it. A pubkey is a string, so it is stable across the
   * churn, and the signer itself is read through a ref at the moment it is
   * used. That keeps this route correct even while the provider is not.
   *
   * WHO IS ACTUALLY IN IT, measured by `cloistr-orchestrator` 2026-09-16 rather
   * than assumed, because the first version of this note said "fleet-wide" and
   * that was too broad. The loop needs all three of: this package's own
   * provider, `autoRestore` left on, and a restored session. Three apps mount a
   * bare `<AuthProvider>` and qualify -- Pages, `cloistr-calendar-ui` and
   * `cloistr-contacts-ui` -- and the other eleven sit behind `@cloistr/ui`'s
   * `SharedAuthProvider`, which passes `autoRestore={false}`.
   *
   * THAT PROP IS THE ONLY THING PROTECTING MOST OF THE FLEET AND IT WAS ADDED
   * FOR AN UNRELATED REASON: stopping a double-connect extension re-prompt.
   * Nobody knew it was load-bearing. Worth knowing before anyone changes it.
   *
   * AND IT IS WHY PAGES IS EXPOSED WHEN MOST APPS ARE NOT. This app is on the
   * bare provider deliberately -- see `RequireSigner`, which explains at length
   * why the shared one would put a sign-in gate in front of every reader. That
   * choice is still right and it is what puts us in the loop's blast radius.
   *
   * Routed P1 to `cloistr-common` with the reproduction; this stays either way,
   * because a route that renders other people's pages should not be one careless
   * dependency away from attacking its own relay.
   */
  const signerRef = useRef(signer);
  signerRef.current = signer;
  const activeKey = authState.pubkey;

  useEffect(() => {
    let live = true;
    setLoad(null);
    // `void`: the promise is deliberately not awaited here and loadPage never
    // rejects -- every failure is already collapsed into { status: 'unavailable' }.
    //
    // THE SIGNER IS PASSED WHEN THERE IS ONE, and its only effect is that a
    // private page opens for the person entitled to it. A reader without one is
    // never asked for anything: they get the public page, which is the ordinary
    // case and the one this screen exists for.
    const active = signerRef.current;
    void loadPage(naddr, active ? { signer: active } : {}).then((result) => {
      if (live) setLoad(result);
    });
    return () => {
      live = false;
    };
  }, [naddr, activeKey]);

  useEffect(() => {
    // CLEARED FIRST, EVERY TIME. Navigating from a themed page to an unthemed
    // one must land in default tokens; carrying the last theme over would style
    // one author's page with another author's choices.
    setTheme(null);
    if (load?.status !== 'ready' || !load.index.themeAddress) return;

    let live = true;
    // Asked of the relays the PAGE was found on rather than ours, because a
    // theme almost always lives beside the pages that use it. A renderer that
    // only ever asked our own relay could not style anybody else's page.
    //
    // `void`: loadTheme never rejects. Every way this comes to nothing -- a
    // malformed coordinate, a coordinate naming something that is not a theme,
    // no relay holding it -- is already null, and null is the page in defaults.
    void loadTheme(load.index.themeAddress, load.relays).then((result) => {
      if (live) setTheme(result);
    });
    return () => {
      live = false;
    };
  }, [load]);

  // Rendering is pure and can be expensive on a long document, so it is keyed
  // on the body rather than redone on every unrelated re-render. An HTML page
  // is NOT run through here: the markdown renderer's job is to sanitise, and
  // sanitising the escape hatch into safety would defeat the point of offering
  // it. `null` for HTML is what routes the body to the sandbox below.
  const rendered = useMemo(
    () =>
      load?.status === 'ready' && load.index.format === 'markdown'
        ? renderMarkdown(load.body, {
            // Our own pages are our format, where raw HTML is shown, not run. A
            // stranger's long-form article is not, and keeps its sanitised HTML.
            rawHtml: load.index.source.kind === 'inline' ? 'sanitise' : 'literal',
          })
        : null,
    [load]
  );

  useEffect(() => {
    document.title = load?.status === 'ready' ? `${load.index.title} — Pages` : 'Pages';
  }, [load]);

  if (load === null) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }

  if (load.status === 'unavailable') {
    return <PageUnavailable />;
  }

  const { index } = load;

  return (
    /* The theme lands HERE and not on `:root`, which is the whole of its
       containment. These custom properties are visible to the page's own
       declarations and to nothing above this element, so a theme restyles the
       article and cannot reach our header, the app switcher or the sign-in
       control. Moving this to a document-level effect would hand a stranger's
       event control of Cloistr's own interface. */
    <article
      className="page"
      data-width={index.layout.width}
      data-columns={index.layout.columns}
      data-themed={theme ? 'true' : undefined}
      data-reader-width={wide ? 'wide' : undefined}
      style={themeStyle(theme, scheme)}
    >
      <header className="page-header">
        {/* The reader's own control, never part of the page. */}
        <WidthToggle
          wide={wide}
          onChange={setWide}
          readingMeasure={
            index.layout.width === 'narrow'
              ? 'var(--pages-measure-narrow)'
              : index.layout.width === 'wide'
                ? 'var(--pages-measure-wide)'
                : 'var(--pages-measure)'
          }
        />
        <h1 className="page-title">{index.title}</h1>
        {index.summary && <p className="page-summary">{index.summary}</p>}
      </header>
      {/* A contents box is built by walking the headings the sanitiser produced,
          which only exists for markdown. An HTML page's headings are inside a
          document with an opaque origin that we cannot read and deliberately
          cannot ask, so its `toc` tag is honoured by doing nothing rather than
          by rendering an empty box. */}
      {rendered ? (
        <>
          {index.layout.toc && <TableOfContents headings={rendered.headings} />}
          <PageBody fragment={rendered.fragment} />
        </>
      ) : (
        <SandboxedHtml html={load.body} title={index.title} />
      )}
      {/* The way out of a single page and into the rest of the author's. No
          name is shown and no profile is fetched for it: a kind 0 lookup on
          every page load would be a second relay round trip paid by every
          reader to render one line of decoration. */}
      <footer className="page-footer">
        <Link to={authorPath(encodeProfile(index.address.pubkey))}>
          More pages by this author
        </Link>
        {/* See `showCanonical` above. Not shown on the canonical route, where
            the address bar already holds exactly this link. */}
        {showCanonical && (
          <Link className="page-canonical-link" to={pagePath(index.address)}>
            Permanent link
          </Link>
        )}
        {/* The way into the editor for an existing page, which is the case the
            editor is designed around. Without it, revising a page means typing
            a URL, and the commonest thing an author does with a page would be
            the one thing the app never offers. Shown only to the key that
            published it, because it is the only key that can save. */}
        {authState.pubkey === index.address.pubkey && (
          <Link className="page-edit-link" to={editPath(index.address)}>
            Edit this page
          </Link>
        )}
        {/* A stranger's long-form article is not ours to package as a Pages
            file, so export is for our own kind only. */}
        {index.source.kind !== 'inline' && <ExportLink index={index} body={load.body} />}
      </footer>
    </article>
  );
}
